/* Launch panel: pick a unit class, pay the launch price from a Solana wallet, follow your agent.
   In DEMO mode (no token configured on the server) launches are free, rate-limited and labelled. */
import { CLASSES, CLASS_DEF, M_PER_PX } from '../shared/config';
import { S } from './state';
import { cells, spritesReady } from './sprites';

const $ = (id: string) => document.getElementById(id)!;
const BLURB: Record<string, string> = {
  digger: 'cuts tunnels fast in soft rock', hauler: 'moves spoil and ore; keeps faces open', scout: 'surveys for deposits and discoveries',
  engineer: 'builds chambers and shoring', driller: 'heavy cutter for hard rock',
};
let cfg: any = null, wallet: string | null = null, cls = 'digger', busy = false, select: (s: any, follow?: boolean) => void;
const esc = (s: string) => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const short = (w: string) => w.startsWith('demo-') ? w : w.slice(0, 4) + '…' + w.slice(-4);
const fmtT = (s: number) => { const h = Math.floor(s / 3600), m = Math.floor(s % 3600 / 60); return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(Math.floor(s % 60)).padStart(2, '0')}s`; };
function getStore(k: string) { try { return localStorage.getItem(k); } catch { return null; } }
function setStore(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } }

function status(msg: string, kind = '') { const s = $('l-status'); s.textContent = msg; s.className = 'note ' + kind; }
function me(): string | null { return wallet || getStore('subterra.demoOwner'); }
/* Paid launches waiting for a final server answer, keyed by signature (shared by all tabs). Entries are
   removed only on success or when the user dismisses a failed one, so a payment is never silently lost. */
const PAYMENTS = 'subterra.payments', MAX_AGE_MS = 23 * 3600_000;
interface Pending { signature: string; wallet: string; cls: string; at: number; failed?: string }
function payments(): Record<string, Pending> { try { return JSON.parse(getStore(PAYMENTS) || '{}') || {}; } catch { return {}; } }
function putPayment(p: Pending) { const m = payments(); m[p.signature] = p; setStore(PAYMENTS, JSON.stringify(m)); }
function dropPayment(sig: string) { const m = payments(); delete m[sig]; setStore(PAYMENTS, JSON.stringify(m)); }
const working = new Set<string>();

export function initLaunch(sel: typeof select) {
  select = sel;
  wallet = getStore('subterra.wallet');
  if (innerWidth < 1100) document.body.classList.add('lclosed');
  $('l-close').onclick = () => document.body.classList.add('lclosed');
  $('l-open').onclick = () => document.body.classList.remove('lclosed');
  $('l-classes').innerHTML = CLASSES.map(c => `<button class="cls${c === cls ? ' on' : ''}" data-cls="${c}"><canvas width="14" height="9"></canvas><div><b>${CLASS_DEF[c].label}</b><span>${BLURB[c]}</span></div></button>`).join('');
  $('l-classes').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest('[data-cls]') as HTMLElement | null; if (!b) return;
    cls = b.dataset.cls!; document.querySelectorAll('.cls').forEach(x => x.classList.toggle('on', x === b));
  });
  $('l-wallet').onclick = connectWallet;
  $('l-go').onclick = launch;
  document.addEventListener('click', e => {
    const d = (e.target as HTMLElement).closest('[data-dismiss]') as HTMLElement | null;
    if (d) { e.preventDefault(); dropPayment(d.dataset.dismiss!); render(); return; }
    const el = (e.target as HTMLElement).closest('[data-agent]') as HTMLElement | null;
    if (el) select({ kind: 'robot', id: +el.dataset.agent! }, true);
  });
  loadConfig().then(resumePending); setInterval(refresh, 5000); setInterval(tick, 1000);
  const t = setInterval(() => { if (spritesReady) { drawIcons(); clearInterval(t); } }, 200);
}
function drawIcons() {
  document.querySelectorAll<HTMLButtonElement>('.cls').forEach(b => {
    const i = CLASSES.indexOf(b.dataset.cls as any), g = b.querySelector('canvas')!.getContext('2d')!;
    g.clearRect(0, 0, 14, 9); g.drawImage(cells[i][0][0][0].c, 0, 0);
  });
}
async function loadConfig() {
  try { cfg = await (await fetch('/api/launch/config', { cache: 'no-store' })).json(); } catch { return; }
  const m = $('l-mode');
  m.textContent = cfg.mode === 'live' ? 'LIVE' : cfg.mode === 'demo' ? 'DEMO' : 'OFF'; m.className = 'tag ' + (cfg.mode === 'live' ? 'ok' : 'demo');
  $('l-price').textContent = cfg.mode === 'demo' ? 'FREE' : cfg.price.toLocaleString('en-US');
  $('l-sym').textContent = cfg.mode === 'demo' ? 'DEMO' : '$' + cfg.symbol;
  $('l-terms').textContent = cfg.mode === 'demo'
    ? `demo launch · ${fmtT(cfg.lifetimeS)} shift · token not configured yet, no payment taken (live price: ${cfg.price.toLocaleString('en-US')} tokens)`
    : `one agent · ${fmtT(cfg.lifetimeS)} shift · paid to treasury ${short(cfg.treasury)} · max ${cfg.maxPerWallet} per wallet`;
  $('l-reward').textContent = 'Rewards: your agent’s work share is recorded for the treasury reward pool. Payouts are not active yet: they start only after an audited, externally signed distribution is set up.';
  $('l-wallet').textContent = wallet ? short(wallet) : cfg.mode === 'demo' ? 'CONNECT WALLET (OPTIONAL)' : 'CONNECT WALLET';
  updateGo(); refresh();
}
function updateGo() {
  const go = $('l-go') as HTMLButtonElement;
  go.disabled = busy || !cfg || cfg.mode === 'off';
  go.textContent = cfg?.mode === 'live' ? `LAUNCH · ${cfg.price.toLocaleString('en-US')} $${cfg.symbol}` : 'LAUNCH DEMO AGENT';
}
async function loadWalletLib() {
  if ((window as any).SubterraWallet) return (window as any).SubterraWallet;
  await new Promise<void>((ok, bad) => { const s = document.createElement('script'); s.src = '/wallet.js'; s.onload = () => ok(); s.onerror = () => bad(new Error('wallet module failed to load')); document.head.appendChild(s); });
  return (window as any).SubterraWallet;
}
async function connectWallet() {
  try {
    status('loading wallet…');
    const W = await loadWalletLib();
    wallet = await W.connect();
    setStore('subterra.wallet', wallet!);
    $('l-wallet').textContent = short(wallet!); status('wallet connected', 'ok'); updateGo(); refresh();
  } catch (e: any) { status(e.message || String(e), 'err'); }
}
async function launch() {
  if (busy || !cfg) return; busy = true; updateGo();
  try {
    if (cfg.mode === 'live') {
      const W = await loadWalletLib();
      /* a remembered address is only for display: paying needs the wallet actually connected */
      if (!W.connected()) { status('connect your wallet…'); wallet = await W.connect(); setStore('subterra.wallet', wallet!); $('l-wallet').textContent = short(wallet!); }
      else wallet = W.connected();
      /* refuse BEFORE paying if the world or this wallet has no free slot */
      const pf = await (await fetch('/api/launch/preflight?wallet=' + encodeURIComponent(wallet!), { cache: 'no-store' })).json();
      if (!pf.ok) throw new Error(pf.error);
      status('preparing transaction…');
      const bh = await (await fetch('/api/launch/blockhash')).json();
      if (bh.error) throw new Error(bh.error);
      status('approve the payment in your wallet…');
      const signature = await W.payLaunch(cfg, bh.blockhash);
      /* remember the payment before asking the server, so a closed tab or a timeout never loses it */
      const p: Pending = { signature, wallet: wallet!, cls, at: Date.now() };
      putPayment(p);
      await submitPaid(p);
    } else {
      status('launching demo agent…');
      const r = await fetch('/api/launch/demo', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ cls, wallet: wallet || getStore('subterra.demoOwner') || undefined }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'launch failed');
      if (j.demo && !wallet) setStore('subterra.demoOwner', j.owner);
      deployed(j);
    }
  } catch (e: any) { status(e.message || String(e), 'err'); }
  finally { busy = false; updateGo(); }
}
function deployed(j: any) { status(`${j.name} deployed · following it now`, 'ok'); pendingFollow = j.id; refresh(); }
/* POST a paid launch until the server gives a final answer. Unconfirmed (202), busy (429), unavailable
   (403/5xx) and network errors are retried with backoff; the entry stays in localStorage meanwhile and
   is resumed on the next visit. A final rejection keeps the entry (with its signature) on screen. */
async function submitPaid(p: Pending) {
  if (working.has(p.signature)) return; working.add(p.signature);
  try {
    for (let attempt = 0; Date.now() - p.at < MAX_AGE_MS; attempt++) {
      status(attempt ? 'still confirming your payment on chain…' : 'payment sent · confirming on chain…');
      let r: Response | null = null, j: any = {};
      try { r = await fetch('/api/launch', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ cls: p.cls, wallet: p.wallet, signature: p.signature }) }); j = await r.json(); } catch { /* offline: retry */ }
      if (r && r.status === 200 && j.id) { dropPayment(p.signature); deployed(j); return; }
      const final = r !== null && !j.retry && ![202, 403, 429].includes(r.status) && r.status < 500;
      if (final) { putPayment({ ...p, failed: j.error || `launch failed (HTTP ${r!.status})` }); render(); throw new Error(j.error || 'launch failed'); }
      await new Promise(ok => setTimeout(ok, Math.min(30000, 2500 + attempt * 1500)));
    }
    putPayment({ ...p, failed: 'not confirmed within 23 h' }); render();
    throw new Error('payment was not confirmed in time; contact the operator with its signature');
  } finally { working.delete(p.signature); }
}
function resumePending() {
  if (cfg?.mode !== 'live') return;
  for (const p of Object.values(payments())) if (!p.failed) submitPaid(p).catch(e => status(e.message || String(e), 'err'));
  render();
}
let pendingFollow = 0, agents: any[] = [], fetchedAt = 0;
async function refresh() {
  const owner = me();
  try {
    const j = await (await fetch('/api/agents' + (owner ? '?wallet=' + encodeURIComponent(owner) : ''), { cache: 'no-store' })).json();
    agents = j.active; fetchedAt = performance.now();
  } catch { return; }
  if (pendingFollow && S.robots.has(pendingFollow)) { select({ kind: 'robot', id: pendingFollow }, true); pendingFollow = 0; }
  render();
}
function tick() {
  if (pendingFollow && S.robots.has(pendingFollow)) { select({ kind: 'robot', id: pendingFollow }, true); pendingFollow = 0; }
  if (agents.length) render();
}
function row(a: any, mine: boolean) {
  const left = Math.max(0, a.left - (performance.now() - fetchedAt) / 1000);
  return `<div class="ag${mine ? ' mine' : ''}" data-agent="${a.id}"><b>${a.name}</b><span class="r">${a.work.toLocaleString('en-US')} wu</span>
    <em>${mine ? fmtT(left) + ' left' : short(a.owner)}${a.demo ? ' · demo' : ''} · ${a.depth.toFixed(0)} m</em><span class="r">${(a.share * 100).toFixed(1)}%</span>
    <span class="sh"><i style="width:${Math.min(100, a.share * 100)}%"></i></span></div>`;
}
function render() {
  const owner = me(), mine = agents.filter(a => a.owner === owner);
  $('l-mine-n').textContent = mine.length ? String(mine.length) : '';
  const pay = Object.values(payments()).map(p => p.failed
    ? `<div class="note err">payment <code>${p.signature}</code> was not accepted: ${esc(p.failed)}. Keep this signature and contact the operator. <a href="#" data-dismiss="${p.signature}">dismiss</a></div>`
    : `<div class="note">payment ${short(p.signature)} · confirming on chain…</div>`).join('');
  $('l-mine').innerHTML = pay + (mine.length ? mine.map(a => row(a, true)).join('') : pay ? '' : '<span class="note">none yet: launch one above</span>');
  $('l-act-n').textContent = `${agents.length}${cfg ? ' / ' + cfg.maxAgents : ''}`;
  $('l-active').innerHTML = agents.length ? agents.slice(0, 25).map(a => row(a, a.owner === owner)).join('') : '<span class="note">no agents launched yet</span>';
}
export function isMine(owner?: string) { return !!owner && owner === me(); }
export { M_PER_PX };
